import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const AchievementShowcase = ({ achievements, userData }) => {
  const [filter, setFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'Todos' },
    { id: 'earned', label: 'Conquistados' },
    { id: 'progress', label: 'Em Progresso' }
  ];

  const getBadgeIcon = (type) => {
    switch (type) {
      case 'attendance': return 'Calendar';
      case 'performance': return 'Target';
      case 'organizer': return 'Users';
      case 'streak': return 'Zap';
      case 'social': return 'Heart';
      default: return 'Award';
    }
  };

  const getRarityColor = (rarity, earned = true) => {
    if (!earned) return 'bg-muted/50 text-muted-foreground border-muted';
    
    switch (rarity) {
      case 'legendary': return 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-yellow-400';
      case 'epic': return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-400';
      case 'rare': return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-blue-400';
      case 'uncommon': return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-green-400';
      default: return 'bg-gradient-to-r from-gray-500 to-slate-500 text-white border-gray-400';
    }
  };

  const getRarityLabel = (rarity) => {
    switch (rarity) {
      case 'legendary': return 'Lendário';
      case 'epic': return 'Épico';
      case 'rare': return 'Raro';
      case 'uncommon': return 'Incomum';
      default: return 'Comum';
    }
  };

  const filteredAchievements = achievements?.filter(achievement => {
    if (filter === 'earned') return achievement?.earnedAt;
    if (filter === 'progress') return !achievement?.earnedAt && achievement?.progress > 0;
    return true;
  });

  const earnedCount = achievements?.filter(a => a?.earnedAt)?.length || 0;
  const progressCount = achievements?.filter(a => !a?.earnedAt && a?.progress > 0)?.length || 0;
  const totalCount = achievements?.length || 0;

  // Brazilian football culture achievements
  const milestones = [
    {
      id: 'craque',
      title: 'Craque da Pelada',
      description: 'Seja MVP em 10 partidas',
      requirement: 10,
      current: userData?.totalMatches >= 50 ? 8 : 3,
      icon: 'Crown',
      color: 'bg-yellow-500'
    },
    {
      id: 'canário',
      title: 'Canário Pistola',
      description: 'Marque 50 gols',
      requirement: 50,
      current: userData?.goals || 0,
      icon: 'Target',
      color: 'bg-green-500'
    },
    {
      id: 'capitão',
      title: 'Capitão da Galera',
      description: 'Organize 20 peladas',
      requirement: 20,
      current: 12,
      icon: 'Users',
      color: 'bg-blue-500'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-lg text-card-foreground">
            Conquistas
          </h2>
          <div className="text-sm text-muted-foreground">
            {earnedCount}/{totalCount}
          </div>
        </div>

        {/* Progress Overview */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center bg-success/10 rounded-lg p-2">
            <div className="text-lg font-bold text-success">{earnedCount}</div>
            <div className="text-xs text-muted-foreground">Conquistados</div>
          </div>
          <div className="text-center bg-warning/10 rounded-lg p-2">
            <div className="text-lg font-bold text-warning">{progressCount}</div>
            <div className="text-xs text-muted-foreground">Em Progresso</div>
          </div>
          <div className="text-center bg-primary/10 rounded-lg p-2">
            <div className="text-lg font-bold text-primary">{userData?.streak || 0}</div>
            <div className="text-xs text-muted-foreground">Sequência</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {filters?.map((filterOption) => (
            <button
              key={filterOption?.id}
              onClick={() => setFilter(filterOption?.id)}
              className={`px-3 py-1 text-xs font-medium rounded transition-standard flex-1 ${
                filter === filterOption?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {filterOption?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="max-h-80 overflow-y-auto">
        <div className="p-4">
          {filteredAchievements?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {filteredAchievements?.map((achievement) => (
                <div
                  key={achievement?.id}
                  className={`relative border-2 rounded-xl p-3 transition-all duration-300 hover:scale-105 ${
                    achievement?.earnedAt 
                      ? getRarityColor(achievement?.rarity, true)
                      : 'bg-muted/30 border-muted'
                  }`}
                >
                  {/* Rarity Badge */}
                  {achievement?.earnedAt && (
                    <div className="absolute -top-1 -right-1 px-2 py-0.5 bg-white/90 text-xs font-bold rounded-full text-black">
                      {getRarityLabel(achievement?.rarity)}
                    </div>
                  )}

                  <div className="text-center">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                      achievement?.earnedAt 
                        ? 'bg-white/20' :'bg-muted'
                    }`}>
                      <Icon 
                        name={getBadgeIcon(achievement?.type)} 
                        size={20} 
                        className={achievement?.earnedAt ? 'text-white' : 'text-muted-foreground'}
                      />
                    </div>
                    
                    <h3 className={`font-bold text-sm mb-1 ${
                      achievement?.earnedAt ? 'text-white' : 'text-card-foreground'
                    }`}>
                      {achievement?.title}
                    </h3>
                    
                    <p className={`text-xs leading-tight ${
                      achievement?.earnedAt ? 'text-white/90' : 'text-muted-foreground'
                    }`}>
                      {achievement?.description}
                    </p>

                    {/* Progress Bar for Incomplete Achievements */}
                    {!achievement?.earnedAt && achievement?.progress > 0 && (
                      <div className="mt-3">
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${achievement?.progress}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {achievement?.progress}% completo
                        </div>
                        
                        {/* Specific Progress Info */}
                        {achievement?.currentGoals !== undefined && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {achievement?.currentGoals}/{achievement?.targetGoals} gols, {achievement?.currentAssists}/{achievement?.targetAssists} assistências
                          </div>
                        )}
                      </div>
                    )}

                    {/* Earned Date */}
                    {achievement?.earnedAt && (
                      <div className="text-xs text-white/80 mt-2">
                        {new Date(achievement?.earnedAt)?.toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
                <Icon name="Award" size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                {filter === 'earned' ?'Nenhum badge conquistado ainda'
                  : filter === 'progress' ?'Nenhum badge em progresso' :'Nenhum badge disponível'
                }
              </p>
            </div>
          )}
        </div>

        {/* Milestones Section */}
        {filter === 'all' && (
          <div className="border-t border-border p-4">
            <h3 className="font-heading font-semibold text-card-foreground mb-3">
              Marcos Especiais 🏆
            </h3>
            <div className="space-y-3">
              {milestones?.map((milestone) => (
                <div key={milestone?.id} className="flex items-center space-x-3 p-2 bg-muted/30 rounded-lg">
                  <div className={`p-2 rounded-lg ${milestone?.color} text-white`}>
                    <Icon name={milestone?.icon} size={16} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-card-foreground text-sm">{milestone?.title}</h4>
                    <p className="text-xs text-muted-foreground">{milestone?.description}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full transition-all duration-300 ${milestone?.color}`}
                          style={{ width: `${Math.min(milestone?.current / milestone?.requirement * 100, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {milestone?.current}/{milestone?.requirement}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementShowcase;